/* eslint-disable @typescript-eslint/unbound-method */
import {
  Body,
  Button,
  Field,
  TextInput,
  VisuallyHiddenInput,
} from '@hover/blueprint';
import type { FormProps } from '@hover/blueprint/dist/declarations/src/components/forms/Form/Form';
import type { BaseSyntheticEvent } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { debounce } from 'src/../script/helpers/debounce';
import type { InferType } from 'yup';

import { LinkExternal } from 'src/shared/components';
import type { LinkExternalProps } from 'src/shared/components/LinkExternal';
import { Form as SharedForm } from 'src/shared/components/forms';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { External } from 'src/shared/route';
import type { ButtonPressed } from 'src/shared/telemetry';
import { buttonPressed } from 'src/shared/telemetry';

import { useProTranslate } from '../hooks';
import { makeLeadValidationSchema } from '../validations/makeValidationSchemas';

export type LeadSheetFormData = InferType<
  ReturnType<typeof makeLeadValidationSchema>
>;

export interface LeadFormData {
  fullName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  postalCode?: string | null;
}

const FooterLink = (props: LinkExternalProps) => (
  <LinkExternal
    _active={{ color: 'inherit', textDecorationColor: 'inherit' }}
    color="inherit"
    fontWeight="body"
    size={300}
    textDecoration="underline"
    {...props}
  />
);

const LeadFormFields = ({
  trackingData,
}: {
  trackingData?: Partial<ButtonPressed>;
}) => {
  const t = useProTranslate();
  const {
    register,
    formState: { errors },
  } = useFormContext<LeadFormData>();

  const fireFormTrackingEvent = () => {
    if (trackingData) {
      buttonPressed({
        ...trackingData,
        button_text: '',
        track_id: 'get-in-touch-form-start',
      });
    }
  };

  const { hasLeadFormFullName, hasLeadFormPostalCode, hasLeadFormPhoneNumber } =
    useEmbedContext();

  return (
    <>
      {hasLeadFormFullName && (
        <Field
          error={errors.fullName?.message}
          label={t('leadSheet.fullName')}
          name="fullName"
        >
          <TextInput
            name="fullName"
            onChange={debounce(fireFormTrackingEvent, 3000)}
            placeholder=""
            ref={register}
          />
        </Field>
      )}
      {hasLeadFormPhoneNumber && (
        <Field
          error={errors.phoneNumber?.message}
          label={t('leadSheet.phoneNumber')}
          name="phoneNumber"
        >
          <TextInput
            name="phoneNumber"
            onChange={debounce(fireFormTrackingEvent, 3000)}
            placeholder=""
            ref={register}
            type="tel"
          />
        </Field>
      )}
      <Field
        error={errors.email?.message}
        label={t('leadSheet.email')}
        name="email"
      >
        <TextInput
          name="email"
          onChange={debounce(fireFormTrackingEvent, 3000)}
          placeholder=""
          ref={register}
        />
      </Field>
      {hasLeadFormPostalCode && (
        <Field
          error={errors.postalCode?.message}
          label={t('leadSheet.postalCode')}
          name="postalCode"
        >
          <TextInput
            name="postalCode"
            onChange={debounce(fireFormTrackingEvent, 3000)}
            placeholder=""
            ref={register}
          />
        </Field>
      )}
    </>
  );
};

interface LeadFormProps extends Omit<FormProps, 'onSubmit'> {
  onSubmit: (
    formData: LeadFormData,
    event: BaseSyntheticEvent | undefined,
  ) => void;
  trackingData?: Partial<ButtonPressed>;
  orgDisplayName?: string;
  submitButtonText?: string;
  externalSubmitButtonFormId?: string;
  hideInternalFormButton?: boolean;
  leadInputRef?: React.RefObject<HTMLInputElement>;
}

export const LeadForm = ({
  onSubmit,
  trackingData,
  orgDisplayName,
  submitButtonText,
  externalSubmitButtonFormId,
  hideInternalFormButton,
  leadInputRef,
  ...props
}: LeadFormProps) => {
  const t = useProTranslate();
  const tShare = useTranslate(TranslationNamespace.SHARED);

  const initialValues = {
    fullName: '',
    phoneNumber: '',
    email: '',
    postalCode: '',
  };

  const { hasLeadFormFullName, hasLeadFormPostalCode, hasLeadFormPhoneNumber } =
    useEmbedContext();

  const validationSchema = makeLeadValidationSchema({
    t: tShare,
    showFullName: hasLeadFormFullName,
    showEmail: true,
    showPostalCode: hasLeadFormPostalCode,
    showPhoneNumber: hasLeadFormPhoneNumber,
  });

  return (
    <SharedForm<LeadSheetFormData>
      externalSubmitButtonFormId={externalSubmitButtonFormId}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      w={1}
      {...props}
    >
      <LeadFormFields trackingData={trackingData} />
      <VisuallyHiddenInput name="leadId" ref={leadInputRef} type="hidden" />
      <Body
        as="span"
        color="neutral.550"
        marginTop={400}
        paddingX={{ base: 1, desktop: 800 }}
        size={300}
        textAlign="center"
      >
        <Trans
          components={{
            PrivacyPolicyLink: (
              <FooterLink href={External.PRIVACY_POLICY.path} />
            ),
          }}
          i18nKey={
            orgDisplayName
              ? 'leadForm.privacyPolicyWithOrgName'
              : 'leadForm.privacyPolicy'
          }
          t={t}
          values={{ orgDisplayName }}
        />
      </Body>
      {!hideInternalFormButton && (
        <Button mt={600} size="large" type="submit">
          {submitButtonText || t('leadForm.submit')}
        </Button>
      )}
    </SharedForm>
  );
};
